// Render-time reads the voice surfaces take from the app shell. Bound once
// from `src/utils/agents-data-wiring.ts`, which `_app` already evaluates.
// Every member is a call-through: nothing here reads an app binding, and
// nothing here imports the app or a `/ui` module.

import type { QueryClient } from '@tanstack/react-query';

interface VoiceRoute {
  pathname: string;
  asPath: string;
  query: Record<string, string | string[] | undefined>;
}

export interface VoiceAppHost {
  subscribeRouteChange: (listener: () => void) => () => void;
  readRoute: () => VoiceRoute;
  usePathname: () => string;
  getQueryClient: () => QueryClient;
  openEvidenceSidebar: () => void;
  useIsEvidenceSidebarOpen: () => boolean;
  isVisitRecordingActive: () => boolean;
  useIsVisitRecordingActive: () => boolean;
  useIsVoiceDictationModeEnabled: () => boolean;
  isProductFeedbackAvailable: () => boolean;
}

let host: VoiceAppHost | null = null;

export const configureVoiceAppHost = (next: VoiceAppHost): void => {
  host = next;
};

/** Fail loudly on unconfigured use — never silently degrade. */
export const getVoiceAppHost = (): VoiceAppHost => {
  if (!host) {
    throw new Error(
      '[agents/voice-app-host] not configured — src/utils/agents-data-wiring.ts binds it'
    );
  }
  return host;
};

/** Test-only reset so a suite cannot leak a double into the next one. */
export const resetVoiceAppHostForTests = (): void => {
  host = null;
};
